/**
 * navProfiles — per-role rail ORGANIZATION resolution.
 *
 * Docket 2026-07-26 §1.2.4: the profile TABLE (six bank personas keyed by a
 * literal tenant-slug list) no longer compiles into app source — rail
 * composition and the post-sign-in landing route arrive TENANT-SCOPED through
 * the typed @v0y4ge/api-client (`getNavComposition`, REQ-091), backed by
 * @v0y4ge/api-mocks (design preview: root vocab-preview-seeds.js). No tenant
 * slug is compared anywhere in this file anymore.
 *
 * The gating MECHANISM is unchanged and stays under the access.ts freeze:
 * profiles match on process-role strings exactly as before, every item still
 * passes its `needs` capability check in the rail, and every route keeps its
 * ROUTE_READ_PERM guard — a served profile only organizes surfaces the role
 * can already open.
 *
 * Resolution precedence (unchanged): assignments.workforce →
 * assignments.storeops → assignments.events (falling back to the flat role).
 * The first served profile whose role matches owns the landing route; profiles
 * matched in the OTHER processes merge their items in behind it (2026-08-10).
 * No served composition yet, or no match → the default grouped rail renders
 * (the docket's honest fallback).
 *
 * Icons are string keys resolved against jrni-ui's Icon set in chrome-rail.
 */
import { getNavComp, type NavCompositionProfile } from "../lib/tenant-vocab";

export interface NavProfileItem {
  id: string;            // rail id (RAIL_ROUTES maps it to a route)
  label: string;         // served English fallback; the rail resolves labelKey through the catalog
  labelKey: string;      // catalog key for the exact rail label
  icon: string;          // key into chrome-rail's PROFILE_ICONS
  needs?: string | string[];
  bottom?: boolean;      // pinned below the divider
  badge?: number;
}
export interface NavProfile {
  landing: string;       // route after sign-in
  items: NavProfileItem[];
  role?: string;         // matched process-role (pins storage key for the Pinned+All trial)
  defaultPins?: string[]; // served pin seed for the trial (2026-08-06 rulings D5/D6)
}

/**
 * Same resolution, but waits for the served composition to arrive (it is fetched
 * per tenant, asynchronously). Sign-in calls this: resolving synchronously right
 * after `signIn()` raced the fetch and fell back to the role landing.
 */
export async function navProfileForUserAsync(
  u: { assignments?: Partial<Record<string, string>> | null; role?: string },
  timeoutMs = 1500,
): Promise<NavProfile | null> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const hit = navProfileForUser(u);
    if (hit) return hit;
    if (Date.now() >= deadline) return null;
    await new Promise((r) => setTimeout(r, 100));
  }
}

export function navProfileForUser(
  u: { assignments?: Partial<Record<string, string>> | null; role?: string },
  _tenantSlug?: string | null, // kept for call-site compatibility; the served composition is already tenant-scoped
): NavProfile | null {
  const profiles: NavCompositionProfile[] = getNavComp()?.profiles ?? [];
  if (!profiles.length) return null;
  const a = (u.assignments || {}) as Record<string, string | undefined>;
  /* Phase 1 (2026-08-05): `queues` was absent, so every served queues profile
   * was dead code — a Queue Manager or Queue Staff persona fell through to the
   * generic rail no matter what the tenant served. Appointments stays out until
   * its role catalog lands with the auth wave (REQ-089). */
  const order: ("workforce" | "storeops" | "queues" | "events")[] = ["workforce", "storeops", "queues", "events"];
  /* Multi-process merge (2026-08-10). The loop used to `return` on the FIRST
   * matching profile, so a persona assigned in two processes — a Branch Manager
   * who is also the tenant's Event Manager, say — got the workforce rail and NO
   * events surfaces at all: their events assignment was invisible in navigation
   * even though `can()` granted every events capability. Now every matching
   * profile contributes its items (deduped by rail id, first wins) while the
   * FIRST match still owns the landing route, the pins storage key and the pin
   * seed. Capability gating is untouched — each item keeps its own `needs`. */
  const matches: { proc: string; hit: NavCompositionProfile }[] = [];
  let unservedNonEvents = false;
  for (const proc of order) {
    const role = proc === "events" ? (a.events ?? u.role) : a[proc];
    if (!role) continue;
    const hit = profiles.find((p) => p.process === proc && p.role === role);
    if (hit) matches.push({ proc, hit });
    else if (proc !== "events") unservedNonEvents = true;
  }
  if (!matches.length) return null;
  /* The unified administrator profile (events · System Administrator) is the
   * whole-estate rail — it already carries a section per process — so when a
   * persona holds it, it owns the landing route and the base ordering no matter
   * which other processes they are also assigned in. */
  const adminIdx = matches.findIndex((m) => m.proc === "events" && m.hit.role === "System Administrator");
  if (adminIdx > 0) matches.unshift(...matches.splice(adminIdx, 1));
  /* Guard: if the persona's PRIMARY process (workforce/storeops/queues) has no
   * served profile and only their events role matched, a profile rail here would
   * REPLACE the default grouped rail with a narrow events rail and strand every
   * workforce surface they hold. The honest fallback is the default grouped rail,
   * which is capability-gated and already shows both processes. The unified
   * administrator profile is exempt — it is broader than the default rail. */
  if (unservedNonEvents && adminIdx < 0 && matches.every((m) => m.proc === "events")) return null;
  const base = matches[0]!.hit;
  const seen = new Set<string>();
  const items = matches.flatMap((m) => m.hit.items).filter((i) => (seen.has(i.id) ? false : (seen.add(i.id), true)));
  const pinSeen = new Set<string>();
  const defaultPins = matches
    .flatMap((m) => m.hit.defaultPins ?? [])
    .filter((p) => (pinSeen.has(p) ? false : (pinSeen.add(p), true)));
  return { landing: base.landing, items, role: base.role, defaultPins: defaultPins.length ? defaultPins : base.defaultPins };
}
